import { useState, useEffect, useMemo } from 'react';
import { Form, message } from 'antd';
import API from '../../../data/api';

/** State and actions for the create/edit tag modal. */
export function useTagFormController({ visible, tag, onSuccess, onMessage }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [hasSosTargets, setHasSosTargets] = useState(false);
    const [colorValue, setColorValue] = useState('#ec4899');
    const [removedModels, setRemovedModels] = useState([]);

    const api = API.Instance();
    const showMessage = useMemo(() => onMessage || ((type, msg) => message[type](msg)), [onMessage]);

    useEffect(() => {
        if (tag) {
            form.setFieldsValue({
                name: tag.name,
                description: tag.description,
                color: tag.color,
                applicableModels: tag.applicableModels,
                sosTargets: tag.sosTargets || []
            });
            setHasSosTargets(tag.hasSosTargets);
            setColorValue(tag.color);
            setRemovedModels([]); // Reset removed models when editing
        } else {
            form.resetFields();
            form.setFieldsValue({
                color: '#ec4899',
                applicableModels: ['Store']
            });
            setHasSosTargets(false);
            setColorValue('#ec4899');
            setRemovedModels([]);
        }
    }, [tag, visible, form]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            // Determine if SOS targets should be cleared
            // SOS targets only apply to Stores, so clear them if Store is not in applicableModels
            const storeIsApplicable = values.applicableModels?.includes('Store');
            const shouldHaveSosTargets = hasSosTargets && storeIsApplicable;

            const payload = {
                ...values,
                color: colorValue,
                sosTargets: shouldHaveSosTargets ? values.sosTargets || [] : []
            };

            if (tag) {
                await api.requestUpdateTag(tag._id, payload);
                showMessage('success', 'Etiqueta actualizada correctamente');
            } else {
                await api.requestCreateTag(payload);
                showMessage('success', 'Etiqueta creada correctamente');
            }
            onSuccess();
        } catch (error) {
            showMessage('error', 'Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // Handle applicableModels change - track removed models and disable SOS if Store is removed
    const handleApplicableModelsChange = (models) => {
        form.setFieldValue('applicableModels', models);

        // Track which models are being removed (only when editing an existing tag)
        if (tag && tag.applicableModels) {
            const removed = tag.applicableModels.filter((m) => !models.includes(m));
            setRemovedModels(removed);
        }

        // If Store is removed, disable SOS targets
        if (!models.includes('Store') && hasSosTargets) {
            setHasSosTargets(false);
            form.setFieldValue('sosTargets', []);
        }
    };

    const changeColor = (color) => setColorValue(color.toHexString());

    return {
        form,
        loading,
        hasSosTargets,
        setHasSosTargets,
        colorValue,
        changeColor,
        removedModels,
        handleSubmit,
        handleApplicableModelsChange
    };
}
